/**
 * Public Chat Hook
 *
 * The conversation on the entry page (/welcome). The API keeps it in the database together with
 * the entry the page showed and the ad origin, so a conversation can be read per ad later and
 * survives a reload or another API pod.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { PublicMessage, GetSessionResponse } from '../public-chat.types';
import type { Attribution } from 'features/engagement/signup/attribution';
import type { ChatEntry } from '../entry';

const API_BASE = '/api/v1/public-chat';
const SESSION_STORAGE_KEY = 'pai-public-session';

interface UsePublicChatOptions {
  entry: ChatEntry;
  attribution?: Attribution;
  /** Called once per answer that brought cards (SHOW_SIGNUP …), for the measurement. */
  onCards?: (cards: string[]) => void;
}

interface UsePublicChatReturn {
  sessionId: string | null;
  messages: PublicMessage[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  sendMessage: (content: string, options?: { chip?: boolean }) => Promise<void>;
  clearError: () => void;
  /** Forget this conversation and start a new one (the "Von vorn" button). */
  restart: () => Promise<void>;
}

const readStoredSessionId = (): string | null => {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
};

const storeSessionId = (id: string | null) => {
  try {
    if (id) localStorage.setItem(SESSION_STORAGE_KEY, id);
    else localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

const browserLocale = () => navigator.language.split('-')[0] || 'de';

export function usePublicChat({ entry, attribution, onCards }: UsePublicChatOptions): UsePublicChatReturn {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<PublicMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initRef = useRef(false);
  const onCardsRef = useRef(onCards);
  onCardsRef.current = onCards;

  const openSession = useCallback(
    async (storedId: string | null) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: storedId, locale: browserLocale(), entry, attribution }),
        });
        if (!response.ok) throw new Error('Failed to initialize session');
        const data: GetSessionResponse = await response.json();
        setSessionId(data.session.sessionId);
        storeSessionId(data.session.sessionId);

        // The page shows its own greeting; only an earlier conversation is loaded.
        if (data.session.messageCount > 0) {
          const historyResponse = await fetch(`${API_BASE}/history/${data.session.sessionId}`);
          if (historyResponse.ok) {
            const historyData = await historyResponse.json();
            setMessages(historyData.messages || []);
          }
        } else {
          setMessages([]);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to connect');
      } finally {
        setIsLoading(false);
      }
    },
    [entry, attribution]
  );

  const sendMessage = useCallback(
    async (content: string, options?: { chip?: boolean }) => {
      if (!sessionId || isSending || !content.trim()) return;

      setIsSending(true);
      setError(null);

      const trimmedContent = content.trim();
      const tempUserMessage: PublicMessage = {
        id: uuidv4(),
        role: 'user',
        content: trimmedContent,
        timestamp: new Date().toISOString(),
      };
      const tempAssistantMessage: PublicMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true,
      };
      setMessages((prev) => [...prev, tempUserMessage, tempAssistantMessage]);

      try {
        const response = await fetch(`${API_BASE}/message/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            message: trimmedContent,
            locale: browserLocale(),
            entry,
            ...(options?.chip ? { chip: true } : {}),
          }),
        });

        if (response.status === 429) {
          const rateLimitData = await response.json();
          setError(`Rate limit reached. Please try again in ${Math.ceil(rateLimitData.retryAfter / 60)} minutes.`);
          setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id && m.id !== tempAssistantMessage.id));
          return;
        }
        if (!response.ok) throw new Error('Failed to send message');

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';
        let streamedContent = '';
        let actualUserMessageId = tempUserMessage.id;
        let actualAssistantMessageId = tempAssistantMessage.id;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'start') {
                if (event.userMessageId) actualUserMessageId = event.userMessageId;
                if (event.messageId) actualAssistantMessageId = event.messageId;
              } else if (event.type === 'chunk' && event.content) {
                streamedContent += event.content;
                // Card markers arrive as text first; hide them until the answer is complete.
                const visible = streamedContent.replace(/\[SHOW_[A-Z]+\]/g, '').replace(/\[[A-Z_]*$/, '');
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === tempAssistantMessage.id || m.id === actualAssistantMessageId
                      ? { ...m, id: actualAssistantMessageId, content: visible, isStreaming: true }
                      : m.id === tempUserMessage.id
                        ? { ...m, id: actualUserMessageId }
                        : m
                  )
                );
              } else if (event.type === 'done') {
                const cleaned = streamedContent.replace(/\[SHOW_[A-Z]+\]/g, '').replace(/\n{3,}/g, '\n\n').trim();
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === tempAssistantMessage.id || m.id === actualAssistantMessageId
                      ? {
                          ...m,
                          id: actualAssistantMessageId,
                          content: cleaned,
                          metadata: event.cards ? { cards: event.cards } : undefined,
                          isStreaming: false,
                        }
                      : m
                  )
                );
                if (event.cards?.length) onCardsRef.current?.(event.cards);
              } else if (event.type === 'error') {
                setError(event.content || 'An error occurred');
              }
            } catch {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      } catch (err: any) {
        setError(err.message || 'Failed to send message');
        setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id && m.id !== tempAssistantMessage.id));
      } finally {
        setIsSending(false);
      }
    },
    [sessionId, isSending, entry]
  );

  const clearError = useCallback(() => setError(null), []);

  const restart = useCallback(async () => {
    storeSessionId(null);
    await openSession(null);
  }, [openSession]);

  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;
      void openSession(readStoredSessionId());
    }
  }, [openSession]);

  return { sessionId, messages, isLoading, isSending, error, sendMessage, clearError, restart };
}

export default usePublicChat;
